import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { Box, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import type { GridAlignment } from "@mui/x-data-grid";
import { type Dispatch, type MouseEvent, type ReactNode, type SetStateAction, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useLatestRef } from "../../../hooks/useLatestRef";
import { INK_BLACK } from "../../../theme/tokens";
import { handleColumnSortClick, type SortClickBehavior, type SortingState } from "../../../utils/multiColumnSorting";

interface SortableDataGridHeaderProps<T> {
	/**
	 * Sort key(s) the header drives. Pass a single key for a one-column sort, or an array
	 * (matching a `MultiSortOption.sortColumns`) so a header click produces the same multi-key
	 * sort entry the popup would — toggling instead of stacking a single-key entry on top.
	 */
	field: keyof T | Array<keyof T>;
	/** Display label for the header. */
	headerName: string;
	/** Current multi-sort state (same reference used by SystemSortingButtonWithPopup). */
	sortState: SortingState<T>[];
	/** State setter (same reference used by SystemSortingButtonWithPopup). */
	setSortState: Dispatch<SetStateAction<SortingState<T>[]>>;
	/**
	 * Optional row-level comparator carried into the sort entry created by a header click.
	 * Use when the column value isn't directly comparable (e.g. arrays/objects) and per-column
	 * `compareValues` won't yield a sensible order.
	 */
	customSortFunction?: (a: T, b: T) => 1 | 0 | -1;
	/**
	 * Optional rich tooltip shown on hover. Wraps the title in MUI Tooltip without
	 * blocking the surrounding click handler — so sort-on-click still fires.
	 */
	tooltipContent?: ReactNode;
	/**
	 * Header-click cycle for `clickBehavior: "stack"` only. Default (true) cycles asc → desc → removed
	 * (the third click drops the column from the sort); false keeps the sort on the column forever.
	 * Ignored under `"replace"`, which always keeps the sort — dropping the only key would leave the
	 * grid in fetch order, which reads as a bug.
	 */
	allowRemovalWhenDescendingToggle?: boolean;
	/**
	 * How a click resolves against existing sort keys. Defaults to "stack" — every click on an
	 * unsorted column inserts a new PRIMARY key and demotes the rest, which is what every existing
	 * caller was written against. Pass "replace" so a plain click makes this column the whole sort
	 * and shift+click appends it as a tie-breaker. See {@link SortClickBehavior}.
	 */
	clickBehavior?: SortClickBehavior;
	/** "replace" only — entries this approves are never dropped by a replace. */
	retainEntry?: (entry: SortingState<T>) => boolean;
	/**
	 * Where the label sits across the header's width — the column's `headerAlign`. The click target always spans the
	 * width, so the label has to be placed inside it rather than by MUI's title container. Absent = the start, as MUI
	 * places an unaligned header.
	 */
	align?: GridAlignment;
}

/** The header cell this component sits in — MUI's, or a hand-built grid's (the People roster). */
const COLUMN_HEADER_SELECTOR = '[role="columnheader"]';

/** Keys that activate a focused column header, as they would a button. */
const ACTIVATE_KEYS = new Set(["Enter", " "]);

/**
 * The label shrinks and ends in an ellipsis when the column is narrower than its name, as MUI's own header title does:
 * otherwise a right-aligned (number) header overflows at its start, and the name loses its first letters.
 */
const LABEL_SX = { fontWeight: 700, fontSize: "inherit", lineHeight: "inherit", minWidth: 0 } as const;

const JUSTIFY_BY_ALIGN: Record<GridAlignment, "flex-start" | "center" | "flex-end"> = {
	left: "flex-start",
	center: "center",
	right: "flex-end",
};

/**
 * A DataGrid column header that integrates with external multi-column sort state.
 *
 * Shows a sort arrow + priority number when the column participates in the current multi-sort.
 * It fills the header's width, so a click anywhere in the header sorts. It also publishes the sort to assistive tech —
 * `aria-sort` on its column header, and the priority as text when several keys sort — and sorts on Enter or Space while
 * the column header itself has keyboard focus.
 * A click stacks this column on top of the existing keys by default; under `clickBehavior: "replace"`
 * it becomes the whole sort instead, with shift+click appending it as a lower-priority tie-breaker.
 *
 * Usage — pass via `renderHeader` on a `GridColDef`:
 * ```tsx
 * renderHeader: () => (
 *   <SortableDataGridHeader field="name" headerName="Name" sortState={sortState} setSortState={setSortState} />
 * )
 * ```
 */
export const SortableDataGridHeader = <T,>({
	field,
	headerName,
	sortState,
	setSortState,
	customSortFunction,
	tooltipContent,
	allowRemovalWhenDescendingToggle = true,
	// DEFAULT: stack — the behavior every existing caller was written against. A grid that wants a
	// header click to mean "sort by this column instead" passes "replace" (SystemDataGrid's `headerClickMode`).
	clickBehavior = "stack",
	retainEntry,
	align,
}: SortableDataGridHeaderProps<T>) => {
	const { t } = useTranslation();
	const rootRef = useRef<HTMLDivElement>(null);
	const fieldCols = Array.isArray(field) ? field : [field];
	const primaryField = fieldCols[0];
	// Match by primary key so the arrow appears on the header that owns the sort, even when
	// the entry uses additional tie-break columns the caller wired in via `field`.
	const sortIndex = sortState.findIndex((s) => s.sortColumns[0] === primaryField);
	const sortEntry = sortIndex >= 0 ? sortState[sortIndex] : null;
	const showPriority = sortState.length > 1 && sortEntry;

	const sortBy = useCallback(
		(shiftKey: boolean) => {
			// shift+click is the only way to build a multi-key sort by mouse once stacking is off.
			const behavior: SortClickBehavior = clickBehavior === "replace" && shiftKey ? "append" : clickBehavior;
			handleColumnSortClick({
				sortState,
				setSortState,
				columns: fieldCols,
				label: headerName,
				customSortFunction,
				allowRemovalWhenDescendingToggle,
				clickBehavior: behavior,
				retainEntry,
			});
		},
		[sortState, setSortState, fieldCols, headerName, customSortFunction, allowRemovalWhenDescendingToggle, clickBehavior, retainEntry],
	);
	const handleClick = useCallback((event: MouseEvent<HTMLDivElement>) => sortBy(event.shiftKey), [sortBy]);

	// MUI's own sort model is unused (SystemDataGrid sorts), so MUI leaves every header at `aria-sort="none"`. It never
	// changes that value, so React never rewrites the attribute and the one written here stands. Rendered outside a
	// column header there is nothing to describe.
	const ariaSort = sortEntry ? (sortEntry.sortOrder === "asc" ? "ascending" : "descending") : "none";
	useLayoutEffect(() => {
		rootRef.current?.closest(COLUMN_HEADER_SELECTOR)?.setAttribute("aria-sort", ariaSort);
	}, [ariaSort]);

	// Keyboard focus lands on the column header cell, not on this content, so the keys are heard there. MUI sorts on
	// Enter only through its own (disabled) sort; ctrl/cmd+Enter stays MUI's column-menu shortcut.
	const sortByRef = useLatestRef(sortBy);
	useEffect(() => {
		const header = rootRef.current?.closest<HTMLElement>(COLUMN_HEADER_SELECTOR);
		if (!header) return;
		const onKeyDown = (event: KeyboardEvent) => {
			// A focused control inside the header (the column-reorder handle) owns its own keys.
			if (event.target !== header || !ACTIVATE_KEYS.has(event.key) || event.ctrlKey || event.metaKey) return;
			event.preventDefault();
			sortByRef.current(event.shiftKey);
		};
		header.addEventListener("keydown", onKeyDown);
		return () => header.removeEventListener("keydown", onKeyDown);
	}, [sortByRef]);

	return (
		<Box
			ref={rootRef}
			onClick={handleClick}
			sx={{
				display: "flex",
				alignItems: "center",
				// DEFAULT: the start — an unaligned MUI header puts its label there.
				justifyContent: JUSTIFY_BY_ALIGN[align ?? "left"],
				gap: 0.5,
				cursor: "pointer",
				userSelect: "none",
				// Grows across the header's title area (a flex row in MUI's header; see SystemDataGrid's sortable-header
				// rule) and fills a block parent by its auto width — so the whole header width is the click target.
				flex: 1,
				minWidth: 0,
				"&:hover": {
					background: alpha(INK_BLACK, 0.04),
					borderRadius: 2,
				},
				px: 0.5,
			}}
		>
			{tooltipContent ? (
				<Tooltip title={tooltipContent} arrow placement="top">
					<Typography variant="body2" noWrap sx={[LABEL_SX, { cursor: "help" }]}>
						{headerName}
					</Typography>
				</Tooltip>
			) : (
				<Typography variant="body2" noWrap sx={LABEL_SX}>
					{headerName}
				</Typography>
			)}
			{sortEntry && (
				<Box sx={{ display: "flex", alignItems: "center", gap: "2px", flexShrink: 0 }}>
					{sortEntry.sortOrder === "asc" ? (
						<ArrowUpwardIcon sx={{ fontSize: 16, color: "primary.main" }} />
					) : (
						<ArrowDownwardIcon sx={{ fontSize: 16, color: "primary.main" }} />
					)}
					{showPriority && (
						<>
							{/* The bare digit would read as part of the column's name; the hidden text says what it is. */}
							<Typography aria-hidden variant="caption" sx={{ fontSize: 10, fontWeight: 700, color: "primary.main", lineHeight: 1 }}>
								{sortIndex + 1}
							</Typography>
							<Box component="span" sx={visuallyHidden}>
								{t("system.dataGrid.sortPriority", { priority: sortIndex + 1 })}
							</Box>
						</>
					)}
				</Box>
			)}
		</Box>
	);
};
